import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import LoadingState from '../../../dashboard/widgets/Shared/LoadingState';
import EmptyState from '../../../dashboard/widgets/Shared/EmptyState';
import './DeviceAuditHistory.css';
import { API_ENDPOINTS } from '../../../../config/apiConfig';

// Field category display names and colors
const CATEGORY_DISPLAY = {
  'technician': { label: '🔧 Technician', color: '#FF9800' },
  'warranty': { label: '📋 Warranty', color: '#2196F3' },
  'maintenance': { label: '⚙️ Maintenance', color: '#4CAF50' },
  'contract': { label: '📄 Contract', color: '#9C27B0' },
  'device_info': { label: 'ℹ️ Device Info', color: '#607D8B' },
  'custody': { label: '👨‍💼 Custody', color: '#E91E63' },
  'cost': { label: '💰 Cost', color: '#FF5722' },
  'other': { label: '📌 Other', color: '#757575' }
};

const formatAuditDateTime = (isoString) => {
  if (!isoString) return '';
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const getCategoryLabelForSearch = (category, availableCategories) => {
  if (!category) return '';
  return (
    availableCategories[category]?.label ||
    CATEGORY_DISPLAY[category]?.label ||
    category
  );
};

const toSearchableString = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).toLowerCase();
};

/** True when a single field change row matches the search query. */
const changeMatchesSearch = (change, searchLower, availableCategories) => {
  const fieldName = change.field_name || '';
  const changeHaystack = [
    fieldName,
    fieldName.replace(/_/g, ' '),
    change.field_category,
    getCategoryLabelForSearch(change.field_category, availableCategories),
    change.old_value,
    change.new_value,
  ]
    .map(toSearchableString)
    .join(' ');

  return changeHaystack.includes(searchLower);
};

const getMatchingChanges = (transaction, searchLower, availableCategories) => {
  if (!searchLower) {
    return transaction.changes || [];
  }
  return (transaction.changes || []).filter((change) =>
    changeMatchesSearch(change, searchLower, availableCategories)
  );
};

function DeviceAuditHistory({ deviceId, token, user }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionFilter, setActionFilter] = useState('all'); // all, CREATE, UPDATE, DELETE
  const [categoryFilter, setCategoryFilter] = useState(''); // Empty string = no filter
  const [expandedTransactions, setExpandedTransactions] = useState(new Set()); // Track which transactions are expanded
  const [searchText, setSearchText] = useState('');
  const [availableCategories, setAvailableCategories] = useState({}); // Dynamically loaded categories
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  // Only admins can view full audit history
  const canViewAudit = user && ['admin', 'superadmin'].includes(user.role);

  useEffect(() => {
    if (canViewAudit) {
      // Fetch available categories for device entity type
      fetchAvailableCategories();
    } else {
      setError('You do not have permission to view audit history');
      setLoading(false);
    }
  }, [canViewAudit]);

  useEffect(() => {
    if (deviceId && canViewAudit) {
      fetchAuditHistory();
    }
  }, [deviceId, canViewAudit, categoryFilter]);

  const fetchAvailableCategories = async () => {
    setCategoriesLoading(true);
    try {
      const response = await axios.get(API_ENDPOINTS.AUDIT.CATEGORIES('device'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Build category display mapping from available categories
      const categoryMap = {};
      const colors = [
        '#FF9800', // orange
        '#2196F3', // blue
        '#4CAF50', // green
        '#9C27B0', // purple
        '#607D8B', // blue-gray
        '#E91E63', // pink
        '#FF5722', // deep orange
        '#757575'  // gray
      ];

      response.data.available_categories.forEach((category, index) => {
        // Use the hardcoded CATEGORY_DISPLAY if available (includes emojis), otherwise create formatted label
        const categoryInfo = CATEGORY_DISPLAY[category];
        categoryMap[category] = {
          label: categoryInfo ? categoryInfo.label : category.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
          color: categoryInfo ? categoryInfo.color : colors[index % colors.length]
        };
      });

      setAvailableCategories(categoryMap);
    } catch (err) {
      console.error('Error fetching available categories:', err);
      // Fall back to hardcoded categories if API call fails
      setAvailableCategories(CATEGORY_DISPLAY);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchAuditHistory = async () => {
    setLoading(true);
    setError('');

    try {
      const url = API_ENDPOINTS.DEVICES.AUDIT_HISTORY(deviceId, categoryFilter);

      const response = await axios.get(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const rows = response.data.transactions || [];
      setTransactions(
        rows.map((txn) => ({
          ...txn,
          changes: Array.isArray(txn.changes) ? txn.changes : [],
        }))
      );
      setExpandedTransactions(new Set()); // Reset expanded state
    } catch (err) {
      console.error('Error fetching audit history:', err);
      if (err.response?.status === 403) {
        setError('You do not have permission to view audit history');
      } else if (err.response?.status === 400) {
        setError(`Invalid filter: ${err.response.data.error}`);
      } else {
        setError('Failed to load audit history');
      }
    } finally {
      setLoading(false);
    }
  };

  // Toggle transaction expansion
  const toggleTransactionExpanded = (transactionId) => {
    const newExpanded = new Set(expandedTransactions);
    if (newExpanded.has(transactionId)) {
      newExpanded.delete(transactionId);
    } else {
      newExpanded.add(transactionId);
    }
    setExpandedTransactions(newExpanded);
  };

  const handleTransactionHeaderKeyDown = (event, transactionId) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleTransactionExpanded(transactionId);
    }
  };

  const formatRoleLabel = (role) => {
    if (!role) return '';
    const normalized = String(role).replace(/_/g, ' ').toLowerCase();
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    return formatAuditDateTime(isoString) || 'N/A';
  };

  // Get action badge style
  const getActionBadgeClass = (actionType) => {
    switch (actionType) {
      case 'CREATE':
        return 'action-badge action-create';
      case 'UPDATE':
        return 'action-badge action-update';
      case 'DELETE':
        return 'action-badge action-delete';
      default:
        return 'action-badge';
    }
  };

  const getActionLabel = (actionType) => {
    switch (actionType) {
      case 'CREATE':
        return 'Created';
      case 'UPDATE':
        return 'Updated';
      case 'DELETE':
        return 'Deleted';
      default:
        return actionType || 'Changed';
    }
  };


  const getCategoryLabel = (category) =>
    getCategoryLabelForSearch(category, availableCategories);

  const trimmedSearchText = searchText.trim();
  const isDefaultView =
    actionFilter === 'all' && categoryFilter === '' && trimmedSearchText === '';

  const filteredTransactions = useMemo(() => {
    const searchLower = trimmedSearchText ? trimmedSearchText.toLowerCase() : '';

    let filtered = transactions;

    if (actionFilter !== 'all') {
      filtered = filtered.filter((txn) => txn.action_type === actionFilter);
    }

    return filtered
      .map((txn) => {
        const matchingChanges = getMatchingChanges(txn, searchLower, availableCategories);
        return { ...txn, matchingChanges };
      })
      .filter((txn) => !searchLower || txn.matchingChanges.length > 0);
  }, [transactions, actionFilter, trimmedSearchText, availableCategories]);

  // When searching, expand rows that have matching field changes so results are visible immediately
  useEffect(() => {
    if (!trimmedSearchText) {
      return;
    }
    setExpandedTransactions(new Set(filteredTransactions.map((txn) => txn.id)));
  }, [trimmedSearchText, filteredTransactions]);

  if (!canViewAudit) {
    return (
      <div className="audit-history-container">
        <div className="permission-denied">
          <div className="permission-icon">🔒</div>
          <div className="permission-message">
            <h4>Access Restricted</h4>
            <p>Only administrators can view the audit history for this device.</p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="audit-history-container">
        <LoadingState message="Loading audit history..." />
      </div>
    );
  }

  const activeFiltersLabel = [
    actionFilter === 'all' ? 'All actions' : actionFilter.charAt(0) + actionFilter.slice(1).toLowerCase(),
    categoryFilter ? getCategoryLabel(categoryFilter) : 'All categories',
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="audit-history-container">
      {/* Header and Controls */}
      <div className="audit-header">
        <div className="audit-section-intro">
          <h4 className="audit-section-title">Change log</h4>
          <p className="audit-section-description">
            All changes recorded for this device, grouped by user action. Expand an entry to
            review field-level before and after values, or use the filters below to narrow results.
          </p>
        </div>
        <div className="audit-stats">
          <div className="stat-item">
            <span className="stat-label">{isDefaultView ? 'Total Actions' : 'Matching Actions'}</span>
            <span className="stat-value">{filteredTransactions.length}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">{isDefaultView ? 'Total Changes' : 'Matching Changes'}</span>
            <span className="stat-value">
              {filteredTransactions.reduce(
                (sum, txn) =>
                  sum +
                  (trimmedSearchText
                    ? txn.matchingChanges?.length || 0
                    : txn.changes?.length || 0),
                0
              )}
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {/* Filter and Search Controls */}
      <div className="audit-controls">
        <div className="audit-controls-row">
          <div className="audit-controls-primary">
            <div
              className="audit-segmented-control"
              role="tablist"
              aria-label="Filter by action"
            >
              {['all', 'CREATE', 'UPDATE', 'DELETE'].map((type) => (
                <button
                  key={type}
                  type="button"
                  className={`audit-segmented-btn ${actionFilter === type ? 'active' : ''}`}
                  onClick={() => setActionFilter(type)}
                  aria-pressed={actionFilter === type}
                >
                  {type === 'all' && 'All'}
                  {type === 'CREATE' && 'Created'}
                  {type === 'UPDATE' && 'Updated'}
                  {type === 'DELETE' && 'Deleted'}
                </button>
              ))}
            </div>

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="category-filter-select audit-control-input"
              disabled={categoriesLoading}
              aria-label="Filter by category"
              title={categoryFilter ? getCategoryLabel(categoryFilter) : 'All categories'}
            >
              <option value="">All categories</option>
              {Object.entries(availableCategories).length > 0 ? (
                Object.entries(availableCategories).map(([key, info]) => (
                  <option key={key} value={key}>
                    {info.label}
                  </option>
                ))
              ) : (
                Object.entries(CATEGORY_DISPLAY).map(([key, info]) => (
                  <option key={key} value={key}>
                    {info.label}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="audit-controls-secondary">
            <div className="audit-search-box" aria-label="Search audit history">
              <span className="search-icon-lens" aria-hidden="true">
                🔍
              </span>
              <input
                type="text"
                placeholder="Search field changes…"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="search-input-clean"
                aria-label="Search audit history"
              />
              {searchText && (
                <button
                  className="clear-search-btn-inline"
                  type="button"
                  onClick={() => setSearchText('')}
                  title="Clear search"
                  aria-label="Clear search"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="audit-controls-footer">
          <div className="audit-controls-summary" aria-label="Active filters">
            {activeFiltersLabel}
          </div>
          <p className="audit-search-hint">
            Finds matching field changes across audit history (field name, category, before/after values). Only matching fields are shown in each action.
          </p>
        </div>
      </div>

      {/* History Timeline */}
      {filteredTransactions.length === 0 ? (
        <EmptyState
          section="Audit history"
          icon="📭"
          message={
            trimmedSearchText
              ? 'No changes match your search criteria.'
              : categoryFilter
                ? `No changes in the ${getCategoryLabel(categoryFilter)} category.`
                : actionFilter !== 'all'
                  ? `No ${actionFilter.toLowerCase()} actions found.`
                  : 'No audit history available for this device.'
          }
        />
      ) : (
        <div className="audit-timeline audit-timeline--cardList">
          {filteredTransactions.map((transaction) => {
            const isExpanded = expandedTransactions.has(transaction.id);
            const totalChangeCount = transaction.changes?.length || 0;
            const displayChanges = trimmedSearchText
              ? transaction.matchingChanges
              : transaction.changes;
            const displayChangeCount = displayChanges?.length || 0;

            return (
              <div key={transaction.id} className="timeline-entry">
                <div className="audit-entry-card">

                {/* Transaction header (clickable to expand) */}
                <div
                  className="transaction-header"
                  onClick={() => toggleTransactionExpanded(transaction.id)}
                  onKeyDown={(e) => handleTransactionHeaderKeyDown(e, transaction.id)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  aria-label={`${isExpanded ? 'Collapse' : 'Expand'} audit action from ${transaction.changed_by_name || 'Unknown'} at ${formatDateTime(transaction.changed_at)}`}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="header-left">
                    <span className={getActionBadgeClass(transaction.action_type)}>
                      {getActionLabel(transaction.action_type)}
                    </span>

                    <div className="change-summary">
                      <span className="change-count">
                        {trimmedSearchText
                          ? `${displayChangeCount} of ${totalChangeCount} field${totalChangeCount !== 1 ? 's' : ''} match`
                          : `${displayChangeCount} field${displayChangeCount !== 1 ? 's' : ''} changed`}
                      </span>
                    </div>
                  </div>

                  <div className="header-right">
                    <div className="timestamp">{formatDateTime(transaction.changed_at)}</div>

                    <div className="audit-userbar">
                      {transaction.changed_by_name !== 'System' ? (
                        <div className="audit-user-card">
                          <span className="audit-user-icon" aria-hidden="true">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path
                                d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" />
                            </svg>
                          </span>
                          <div className="audit-user-details">
                            <div
                              className="audit-user-name"
                              title={transaction.changed_by_name || 'Unknown'}
                            >
                              {transaction.changed_by_name || 'Unknown'}
                            </div>
                            <div className="audit-user-meta">
                              {transaction.changed_by_employee_id && (
                                <span className="audit-user-id">
                                  ID: {transaction.changed_by_employee_id}
                                </span>
                              )}
                              {transaction.changed_by_role && (
                                <span
                                  className={`audit-user-role audit-user-role--${transaction.changed_by_role?.toLowerCase()}`}
                                >
                                  {formatRoleLabel(transaction.changed_by_role)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="audit-user-card audit-user-card--system">
                          <span className="audit-user-icon audit-user-icon--system" aria-hidden="true">
                            ⚙️
                          </span>
                          <div className="audit-user-details">
                            <div className="audit-user-name">System</div>
                          </div>
                        </div>
                      )}
                    </div>

                    <button
                      className={`expand-btn ${isExpanded ? 'expanded' : ''}`}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleTransactionExpanded(transaction.id);
                      }}
                      title={isExpanded ? 'Collapse' : 'Expand'}
                      aria-label={isExpanded ? 'Collapse transaction' : 'Expand transaction'}
                    >
                      <span className={`expand-chevron ${isExpanded ? 'is-expanded' : ''}`} aria-hidden="true" />
                    </button>
                  </div>
                </div>

                {/* Transaction details (expandable) - Table format */}
                {isExpanded && (
                  <div className="transaction-details">
                    <div className="changes-table-container">
                      <table className="changes-table">
                        <thead>
                          <tr>
                            <th>Field</th>
                            <th>Category</th>
                            <th>Before</th>
                            <th>After</th>
                          </tr>
                        </thead>
                        <tbody>
                          {displayChanges.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="audit-no-matching-fields">
                                No field changes match your search in this action.
                              </td>
                            </tr>
                          ) : (
                          displayChanges.map((change, changeIndex) => (
                            <tr key={`${change.field_name}-${changeIndex}`} className="change-row">
                              <td className="field-cell">{change.field_name}</td>
                              <td className="category-cell">
                                <span className="change-category-label">
                                  {getCategoryLabel(change.field_category)}
                                </span>
                              </td>
                              <td className="before-cell">
                                {change.old_value !== null && change.old_value !== undefined ? (
                                  <span className="audit-cell-value audit-cell-value--before">
                                    {String(change.old_value)}
                                  </span>
                                ) : (
                                  <span className="value-empty">—</span>
                                )}
                              </td>
                              <td className="after-cell">
                                {change.new_value !== null && change.new_value !== undefined ? (
                                  <span className="audit-cell-value audit-cell-value--after">
                                    {String(change.new_value)}
                                  </span>
                                ) : (
                                  <span className="value-empty">—</span>
                                )}
                              </td>
                            </tr>
                          )))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer info */}
      <div className="audit-footer">
        <p className="audit-info">
          Showing <strong>{filteredTransactions.length}</strong> of <strong>{transactions.length}</strong> audit actions
          {categoryFilter && ` (filtered by ${getCategoryLabel(categoryFilter)})`}
        </p>
        <p className="audit-note">
          {trimmedSearchText
            ? 'ℹ️ Search shows only field changes that match. Each action lists matching fields when expanded.'
            : 'ℹ️ Each action groups multiple field changes together. Click an action to see all field changes. Audit history is immutable and cannot be edited or deleted.'}
        </p>
      </div>
    </div>
  );
}

export default DeviceAuditHistory;
